import React from "react";
import { Phone, Mail, MapPin, MessageCircle, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useSiteContent } from "../../context/SiteContent";

export const Contact = ({ onQuote }) => {
  const { content } = useSiteContent();
  const ci = content?.companyInfo || {};
  const wa = `https://wa.me/${ci.whatsappNumber || "919998147181"}`;
  const people = [
    { name: ci.contact1Name, phone: ci.contact1Phone },
    { name: ci.contact2Name, phone: ci.contact2Phone },
  ].filter((p) => p.name);

  return (
    <section id="contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="rounded-[2rem] bg-helios-slate relative overflow-hidden p-8 md:p-14">
          <div className="noise-overlay" />
          <div className="relative grid lg:grid-cols-2 gap-12">
            <Reveal>
              <p className="overline text-helios-amber mb-4">Contact</p>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
                Ready to recover<br />your lost generation?
              </h2>
              <p className="mt-5 text-slate-300 max-w-md leading-relaxed">
                Talk to the Helios engineering team about a plant audit, a custom quote, or a full feasibility study for your site.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={onQuote}
                  className="btn-sheen inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-helios-slate hover:bg-helios-mist transition-colors"
                  data-testid="contact-quote">
                  Request a Quote <ArrowRight size={16} />
                </button>
                <a href={wa} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                  data-testid="contact-whatsapp">
                  <MessageCircle size={16} /> WhatsApp Us
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="space-y-5">
                {people.map((p) => (
                  <a key={p.phone} href={`tel:${(p.phone || "").replace(/\s/g, "")}`}
                    className="flex items-center gap-4 rounded-xl bg-white/5 border border-white/10 p-4 hover:bg-white/10 transition-colors"
                    data-testid={`contact-person-${(p.phone || "").replace(/\s/g, "")}`}>
                    <div className="flex items-center justify-center w-11 h-11 rounded-full bg-helios-cyan/20 text-helios-cyan shrink-0"><Phone size={18} /></div>
                    <div>
                      <p className="font-display font-bold text-white">{p.name}</p>
                      <p className="font-mono text-sm text-slate-300">{p.phone}</p>
                    </div>
                  </a>
                ))}
                <a href={`mailto:${ci.email}`} className="flex items-center gap-4 rounded-xl bg-white/5 border border-white/10 p-4 hover:bg-white/10 transition-colors" data-testid="contact-email">
                  <div className="flex items-center justify-center w-11 h-11 rounded-full bg-helios-amber/20 text-helios-amber shrink-0"><Mail size={18} /></div>
                  <p className="font-mono text-sm text-slate-200 break-all">{ci.email}</p>
                </a>
                <div className="flex items-start gap-4 rounded-xl bg-white/5 border border-white/10 p-4">
                  <div className="flex items-center justify-center w-11 h-11 rounded-full bg-white/10 text-white shrink-0"><MapPin size={18} /></div>
                  <p className="text-sm text-slate-300 leading-relaxed">{ci.worksAddress}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
